<script>
import { mapGetters } from 'vuex';
import Draggable from 'vuedraggable';
import FilterInputBox from 'dashboard/components/widgets/FilterInput/Index.vue';
import AutomationActionInput from 'dashboard/components/widgets/AutomationActionInput.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { useAutomation } from 'dashboard/composables/useAutomation';
import { validateAutomation } from 'dashboard/helper/validations';
import {
  generateAutomationPayload,
  getAttributes,
  getInputType,
  getOperators,
  getCustomAttributeType,
  showActionInput,
} from 'dashboard/helper/automationHelper';
import { AUTOMATION_RULE_EVENTS, AUTOMATION_ACTION_TYPES } from './constants';

const start_value = {
  name: null,
  description: null,
  event_name: 'conversation_created',
  conditions: [
    {
      attribute_key: 'status',
      filter_operator: 'equal_to',
      values: '',
      query_operator: 'and',
      custom_attribute_type: '',
    },
  ],
  actions: [
    {
      action_name: 'assign_agent',
      action_params: [],
    },
  ],
};

// descKey only — all color classes are in the template for reliable Tailwind JIT scanning
const TRIGGER_EVENT_DESCS = {
  conversation_created: 'AUTOMATION.EVENTS.CONVERSATION_CREATED_DESC',
  conversation_updated: 'AUTOMATION.EVENTS.CONVERSATION_UPDATED_DESC',
  message_created: 'AUTOMATION.EVENTS.MESSAGE_CREATED_DESC',
  conversation_opened: 'AUTOMATION.EVENTS.CONVERSATION_OPENED_DESC',
  conversation_idle: 'AUTOMATION.EVENTS.CONVERSATION_IDLE_DESC',
};

export default {
  components: {
    Draggable,
    FilterInputBox,
    AutomationActionInput,
    NextButton,
  },
  props: {
    onClose: {
      type: Function,
      default: () => {},
    },
  },
  emits: ['saveAutomation'],
  setup() {
    const {
      automation,
      automationTypes,
      onEventChange,
      getConditionDropdownValues,
      appendNewCondition,
      appendNewAction,
      removeFilter,
      removeAction,
      resetFilter,
      resetAction,
      getActionDropdownValues,
      manifestCustomAttributes,
    } = useAutomation(start_value);
    return {
      automation,
      automationTypes,
      onEventChange,
      getConditionDropdownValues,
      appendNewCondition,
      appendNewAction,
      removeFilter,
      removeAction,
      resetFilter,
      resetAction,
      getActionDropdownValues,
      manifestCustomAttributes,
    };
  },
  data() {
    return {
      allCustomAttributes: [],
      mode: 'create',
      errors: {},
      triggerEventDescs: TRIGGER_EVENT_DESCS,
      actionKeys: new WeakMap(),
      nextActionKey: 0,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    automationRuleEvents() {
      return AUTOMATION_RULE_EVENTS.map(event => ({
        ...event,
        value: this.$t(`AUTOMATION.EVENTS.${event.value}`),
      }));
    },
    hasAutomationMutated() {
      if (
        this.automation.conditions[0].values ||
        this.automation.actions[0].action_params.length
      )
        return true;
      return false;
    },
    automationActionTypes() {
      const actionTypes = this.isFeatureEnabled('sla')
        ? AUTOMATION_ACTION_TYPES
        : AUTOMATION_ACTION_TYPES.filter(({ key }) => key !== 'add_sla');

      return actionTypes.map(action => ({
        ...action,
        label: this.$t(`AUTOMATION.ACTIONS.${action.label}`),
      }));
    },
    ruleSummaryParts() {
      const event = this.automationRuleEvents.find(
        e => e.key === this.automation.event_name
      );
      const eventLabel = event ? event.value : this.automation.event_name;

      const condTexts = (this.automation.conditions || [])
        .filter(c => c.attribute_key)
        .map(c => this.getConditionSummary(c))
        .filter(Boolean);

      const actionTexts = (this.automation.actions || [])
        .map(a => this.getActionSummary(a))
        .filter(Boolean);

      const and = this.$t('AUTOMATION.FORM.AND_OPERATOR');
      const parts = [];
      if (eventLabel) {
        parts.push({
          text: this.$t('AUTOMATION.FORM.SUMMARY_WHEN_KEYWORD'),
          cls: 'text-n-teal-9 font-semibold',
        });
        parts.push({ text: ` ${eventLabel}` });
      }
      if (condTexts.length) {
        if (parts.length) parts.push({ text: ' → ' });
        parts.push({
          text: this.$t('AUTOMATION.FORM.SUMMARY_IF_KEYWORD'),
          cls: 'text-n-iris-9 font-semibold',
        });
        parts.push({ text: ` ${condTexts.join(` ${and} `)}` });
      }
      if (actionTexts.length) {
        if (parts.length) parts.push({ text: ' → ' });
        parts.push({
          text: this.$t('AUTOMATION.FORM.SUMMARY_THEN_KEYWORD'),
          cls: 'text-n-blue-9 font-semibold',
        });
        parts.push({ text: ` ${actionTexts.join(', ')}` });
      }
      return parts;
    },
  },
  mounted() {
    this.$store.dispatch('inboxes/get');
    this.$store.dispatch('agents/get');
    this.$store.dispatch('contacts/get');
    this.$store.dispatch('teams/get');
    this.$store.dispatch('labels/get');
    this.$store.dispatch('campaigns/get');
    this.allCustomAttributes = this.$store.getters['attributes/getAttributes'];
    this.manifestCustomAttributes();
  },
  methods: {
    getAttributes,
    getInputType,
    getOperators,
    getCustomAttributeType,
    showActionInput,
    actionKey(action) {
      if (!this.actionKeys.has(action)) {
        this.actionKeys.set(action, this.nextActionKey);
        this.nextActionKey += 1;
      }

      return this.actionKeys.get(action);
    },
    updateAction(index, updatedAction) {
      Object.assign(this.automation.actions[index], updatedAction);
    },
    isFeatureEnabled(flag) {
      return this.isFeatureEnabledonAccount(this.accountId, flag);
    },
    selectEvent(key) {
      this.automation.event_name = key;
      this.onEventChange(this.automation);
    },
    emitSaveAutomation() {
      this.errors = validateAutomation(this.automation);
      if (Object.keys(this.errors).length === 0) {
        const automation = generateAutomationPayload(this.automation);
        this.$emit('saveAutomation', automation, this.mode);
      }
    },
    getConditionSummary(condition) {
      if (!condition.attribute_key) return '';
      const attrs = this.getTranslatedAttributes(
        this.automationTypes,
        this.automation.event_name
      );
      const attr = attrs.find(a => a.key === condition.attribute_key);
      const attrName = attr ? attr.name : condition.attribute_key;

      const ops =
        getOperators(
          this.allCustomAttributes,
          this.automationTypes,
          this.automation,
          this.mode,
          condition.attribute_key
        ) || [];
      const op = ops.find(o => o.value === condition.filter_operator);
      const opLabel = op ? op.label : condition.filter_operator;

      const rawVals = []
        .concat(condition.values || [])
        .filter(v => v !== '' && v != null);
      if (!rawVals.length) return [attrName, opLabel].filter(Boolean).join(' ');

      const dropdownVals =
        this.getConditionDropdownValues(condition.attribute_key) || [];
      const valLabels = rawVals.map(v => {
        if (v && typeof v === 'object')
          return v.name || v.title || String(v.id);
        const opt = dropdownVals.find(o => String(o.id) === String(v));
        return opt ? opt.name || opt.title : String(v);
      });
      if (
        condition.attribute_key === 'content' &&
        condition.filter_operator === 'contains'
      ) {
        return this.$t('AUTOMATION.CONDITION.MESSAGE_CONTAINS_ANY_SUMMARY', {
          keywords: valLabels.join(', '),
        });
      }
      return [attrName, opLabel, valLabels.join(', ')]
        .filter(Boolean)
        .join(' ');
    },
    getActionSummary(action) {
      const found = this.automationActionTypes.find(
        t => t.key === action.action_name
      );
      const actionName = found ? found.label : action.action_name;
      const params = []
        .concat(action.action_params || [])
        .filter(p => p !== '' && p != null);
      if (!params.length) return actionName;

      const dropdownVals =
        this.getActionDropdownValues(action.action_name) || [];
      const labels = params
        .map(p => {
          if (p && typeof p === 'object') return p.name || p.title || null;
          const opt = dropdownVals.find(o => String(o.id) === String(p));
          return opt ? opt.name || opt.title : null;
        })
        .filter(Boolean);
      return labels.length ? `${actionName}: ${labels.join(', ')}` : actionName;
    },
    getTranslatedAttributes(type, event) {
      return getAttributes(type, event).map(attribute => {
        const skipTranslation =
          attribute.customAttributeType ||
          [
            'contact_custom_attribute',
            'conversation_custom_attribute',
          ].includes(attribute.key);

        return {
          ...attribute,
          name: skipTranslation
            ? attribute.name
            : this.$t(`AUTOMATION.ATTRIBUTES.${attribute.name}`),
        };
      });
    },
  },
};
</script>

<template>
  <div class="flex flex-col h-full min-h-0">
    <!-- Fixed header -->
    <div
      class="flex items-start gap-4 px-6 py-5 border-b border-n-weak shrink-0"
    >
      <div
        class="size-9 rounded-xl bg-woot-100 dark:bg-woot-900/30 flex items-center justify-center shrink-0 mt-0.5"
      >
        <span class="i-lucide-zap size-5 text-n-blue-9" />
      </div>
      <div>
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ $t('AUTOMATION.ADD.TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-9 mt-0.5">
          {{ $t('AUTOMATION.FORM.SUBTITLE') }}
        </p>
      </div>
    </div>

    <!-- Scrollable body -->
    <div class="flex-1 overflow-y-auto px-6 py-5">
      <!-- Name + Description row -->
      <div class="grid grid-cols-2 gap-3 mb-6">
        <woot-input
          v-model="automation.name"
          :label="$t('AUTOMATION.ADD.FORM.NAME.LABEL')"
          type="text"
          :class="{ error: errors.name }"
          :error="errors.name ? $t('AUTOMATION.ADD.FORM.NAME.ERROR') : ''"
          :placeholder="$t('AUTOMATION.ADD.FORM.NAME.PLACEHOLDER')"
        />
        <woot-input
          v-model="automation.description"
          :label="$t('AUTOMATION.ADD.FORM.DESC.LABEL')"
          type="text"
          :placeholder="$t('AUTOMATION.ADD.FORM.DESC.PLACEHOLDER')"
        />
      </div>

      <!-- WHEN section -->
      <div class="flex gap-3">
        <!-- Rail: step 1 -->
        <div class="flex flex-col items-center shrink-0 w-7">
          <div
            class="size-7 rounded-full flex items-center justify-center shrink-0 bg-n-teal-3"
          >
            <span class="i-lucide-zap size-3.5 text-n-teal-9" />
          </div>
          <div class="w-0.5 flex-1 bg-n-weak my-1" />
        </div>
        <!-- Content -->
        <div class="flex-1 pb-5">
          <p class="text-sm font-bold text-n-teal-9 mb-3">
            {{ $t('AUTOMATION.FORM.WHEN_LABEL') }}
          </p>
          <!-- Rich event picker — all color classes hardcoded for Tailwind JIT -->
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button
              v-for="event in automationRuleEvents"
              :key="event.key"
              type="button"
              class="flex items-start gap-3 p-3 rounded-xl border bg-n-solid-1 text-left transition-all duration-150"
              :class="{
                'border-n-teal-9':
                  automation.event_name === event.key &&
                  event.key === 'conversation_created',
                'border-n-amber-9':
                  automation.event_name === event.key &&
                  event.key === 'conversation_updated',
                'border-n-blue-9':
                  automation.event_name === event.key &&
                  event.key === 'message_created',
                'border-n-iris-9':
                  automation.event_name === event.key &&
                  event.key === 'conversation_opened',
                'border-n-ruby-9':
                  automation.event_name === event.key &&
                  event.key === 'conversation_idle',
                'border-n-weak hover:border-n-strong':
                  automation.event_name !== event.key,
              }"
              @click="selectEvent(event.key)"
            >
              <div
                class="size-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                :class="{
                  'bg-n-teal-3': event.key === 'conversation_created',
                  'bg-n-amber-3': event.key === 'conversation_updated',
                  'bg-n-blue-3': event.key === 'message_created',
                  'bg-n-iris-3': event.key === 'conversation_opened',
                  'bg-n-ruby-3': event.key === 'conversation_idle',
                }"
              >
                <span
                  class="size-3.5"
                  :class="{
                    'i-lucide-message-circle-plus text-n-teal-9':
                      event.key === 'conversation_created',
                    'i-lucide-refresh-cw text-n-amber-9':
                      event.key === 'conversation_updated',
                    'i-lucide-message-square-plus text-n-blue-9':
                      event.key === 'message_created',
                    'i-lucide-folder-open text-n-iris-9':
                      event.key === 'conversation_opened',
                    'i-lucide-clock text-n-ruby-9':
                      event.key === 'conversation_idle',
                  }"
                />
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-xs font-semibold text-n-slate-12 leading-snug">
                  {{ event.value }}
                </p>
                <p class="text-[11px] text-n-slate-9 mt-0.5 leading-snug">
                  {{ $t(triggerEventDescs[event.key]) }}
                </p>
              </div>
              <span
                v-if="automation.event_name === event.key"
                class="i-lucide-check-circle-2 size-4 shrink-0 mt-0.5"
                :class="{
                  'text-n-teal-9': event.key === 'conversation_created',
                  'text-n-amber-9': event.key === 'conversation_updated',
                  'text-n-blue-9': event.key === 'message_created',
                  'text-n-iris-9': event.key === 'conversation_opened',
                  'text-n-ruby-9': event.key === 'conversation_idle',
                }"
              />
            </button>
          </div>
          <p v-if="errors.event_name" class="text-xs text-n-ruby-9 mt-1">
            {{ $t('AUTOMATION.ADD.FORM.EVENT.ERROR') }}
          </p>
          <p
            v-if="hasAutomationMutated"
            class="text-xs text-n-teal-10 mt-2 text-right"
          >
            {{ $t('AUTOMATION.FORM.RESET_MESSAGE') }}
          </p>
        </div>
      </div>

      <!-- IF section -->
      <div class="flex gap-3">
        <!-- Rail: step 2 -->
        <div class="flex flex-col items-center shrink-0 w-7">
          <div
            class="size-7 rounded-full flex items-center justify-center shrink-0 bg-n-iris-3"
          >
            <span class="i-lucide-filter size-3.5 text-n-iris-9" />
          </div>
          <div class="w-0.5 flex-1 bg-n-weak my-1" />
        </div>
        <!-- Content -->
        <div class="flex-1 pb-5">
          <div class="flex items-center gap-2 mb-3">
            <p class="text-sm font-bold text-n-iris-9">
              {{ $t('AUTOMATION.FORM.IF_LABEL') }}
            </p>
            <span
              class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-9 uppercase tracking-wide"
            >
              {{ $t('AUTOMATION.FORM.IF_OPTIONAL') }}
            </span>
          </div>
          <!-- Conditions with AND chips -->
          <div class="flex flex-col gap-2">
            <template v-for="(condition, i) in automation.conditions" :key="i">
              <FilterInputBox
                v-model="automation.conditions[i]"
                :filter-attributes="
                  getTranslatedAttributes(
                    automationTypes,
                    automation.event_name
                  )
                "
                :input-type="
                  getInputType(
                    allCustomAttributes,
                    automationTypes,
                    automation,
                    automation.conditions[i].attribute_key
                  )
                "
                :operators="
                  getOperators(
                    allCustomAttributes,
                    automationTypes,
                    automation,
                    mode,
                    automation.conditions[i].attribute_key
                  )
                "
                :dropdown-values="
                  getConditionDropdownValues(
                    automation.conditions[i].attribute_key
                  )
                "
                :show-query-operator="false"
                :custom-attribute-type="
                  getCustomAttributeType(
                    automationTypes,
                    automation,
                    automation.conditions[i].attribute_key
                  )
                "
                :error-message="
                  errors[`condition_${i}`]
                    ? $t(`AUTOMATION.ERRORS.${errors[`condition_${i}`]}`)
                    : ''
                "
                @reset-filter="resetFilter(i, automation.conditions[i])"
                @remove-filter="removeFilter(i)"
              />
              <div
                v-if="i < automation.conditions.length - 1"
                class="flex items-center gap-2"
              >
                <div class="h-px flex-1 bg-n-weak" />
                <span
                  class="text-[10px] font-bold text-n-slate-9 px-2 py-0.5 rounded-full border border-n-weak bg-n-solid-1 tracking-widest"
                >
                  {{ $t('AUTOMATION.FORM.AND_OPERATOR') }}
                </span>
                <div class="h-px flex-1 bg-n-weak" />
              </div>
            </template>
          </div>
          <div class="mt-2">
            <NextButton
              icon="i-lucide-plus"
              blue
              faded
              sm
              :label="$t('AUTOMATION.ADD.CONDITION_BUTTON_LABEL')"
              @click="appendNewCondition"
            />
          </div>
        </div>
      </div>

      <!-- THEN section -->
      <div class="flex gap-3">
        <!-- Rail: step 3 (no line below) -->
        <div class="flex flex-col items-center shrink-0 w-7">
          <div
            class="size-7 rounded-full flex items-center justify-center shrink-0 bg-n-blue-3"
          >
            <span class="i-lucide-play size-3.5 text-n-blue-9" />
          </div>
        </div>
        <!-- Content -->
        <div class="flex-1">
          <div class="flex items-center justify-between gap-3 mb-3">
            <p class="text-sm font-bold text-n-blue-9">
              {{ $t('AUTOMATION.FORM.THEN_LABEL') }}
            </p>
            <p class="text-xs text-n-slate-9">
              {{ $t('AUTOMATION.FORM.ACTIONS_REORDER_HELP') }}
            </p>
          </div>
          <!-- Actions -->
          <Draggable
            v-model="automation.actions"
            :item-key="actionKey"
            animation="150"
            ghost-class="opacity-50"
            handle=".automation-action-drag-handle"
            tag="div"
            class="flex flex-col gap-2"
          >
            <template #item="{ index: i }">
              <div class="flex items-start gap-1">
                <span
                  :title="$t('AUTOMATION.FORM.DRAG_ACTION')"
                  class="automation-action-drag-handle shrink-0 mt-3 p-1 text-n-slate-9 cursor-grab active:cursor-grabbing"
                >
                  <span
                    class="i-lucide-grip-vertical size-4"
                    aria-hidden="true"
                  />
                </span>
                <AutomationActionInput
                  :model-value="automation.actions[i]"
                  class="flex-1 min-w-0"
                  :action-types="automationActionTypes"
                  :dropdown-values="
                    getActionDropdownValues(automation.actions[i].action_name)
                  "
                  :show-action-input="
                    showActionInput(
                      automationActionTypes,
                      automation.actions[i].action_name
                    )
                  "
                  :error-message="
                    errors[`action_${i}`]
                      ? $t(`AUTOMATION.ERRORS.${errors[`action_${i}`]}`)
                      : ''
                  "
                  @update:model-value="updateAction(i, $event)"
                  @reset-action="resetAction(i)"
                  @remove-action="removeAction(i)"
                />
              </div>
            </template>
          </Draggable>
          <div class="mt-2">
            <NextButton
              icon="i-lucide-plus"
              blue
              faded
              sm
              :label="$t('AUTOMATION.ADD.ACTION_BUTTON_LABEL')"
              @click="appendNewAction"
            />
          </div>
        </div>
      </div>

      <!-- Rule summary -->
      <div class="mt-4 p-4 rounded-xl border border-n-weak bg-n-alpha-1">
        <p
          class="text-[10px] font-semibold uppercase tracking-widest text-n-slate-9 mb-1.5"
        >
          {{ $t('AUTOMATION.FORM.RULE_SUMMARY_LABEL') }}
        </p>
        <p class="text-sm text-n-slate-11 leading-relaxed">
          <span
            v-for="(part, idx) in ruleSummaryParts"
            :key="idx"
            :class="part.cls"
            >{{ part.text }}</span
          >
        </p>
      </div>
    </div>

    <!-- Fixed footer -->
    <div
      class="flex items-center justify-end gap-2 px-6 py-4 border-t border-n-weak shrink-0"
    >
      <NextButton
        faded
        slate
        type="reset"
        :label="$t('AUTOMATION.ADD.CANCEL_BUTTON_TEXT')"
        @click.prevent="onClose"
      />
      <NextButton
        solid
        blue
        type="submit"
        :label="$t('AUTOMATION.ADD.SUBMIT')"
        @click="emitSaveAutomation"
      />
    </div>
  </div>
</template>
